/**
 * MCP-compliant tool wrappers
 *
 * Converts internal tool implementations to MCP SDK format with standardized
 * response structure. Each wrapper transforms the tool output to MCP's expected
 * format: { content: [{ type: 'text', text: string }], structuredContent?: unknown }.
 *
 * All wrappers provide both formatted Markdown output (for display) and structured
 * data (for programmatic access by MCP clients).
 */
import { type Pool } from 'pg';

import { type IndexingOrchestrator } from '@indexing/orchestrator';
import { deleteRepositoryTool, formatDeletionOutput, type DeleteRepositoryInput } from '@mcp/delete-repository';
import { findCrossServiceCallsTool, type FindCrossServiceCallsInput } from '@mcp/find-cross-service-calls';
import { findCrossWorkspaceUsagesTool, type FindCrossWorkspaceUsagesInput } from '@mcp/find-cross-workspace-usages';
import { findSymbolTool, type FindSymbolInput } from '@mcp/find-symbol';
import { getFileContextTool, type GetFileContextInput } from '@mcp/get-file-context';
import { getServiceContextTool, type GetServiceContextInput } from '@mcp/get-service-context';
import { getWorkspaceContextTool, type GetWorkspaceContextInput } from '@mcp/get-workspace-context';
import { formatIndexDocumentationOutput, indexDocumentationTool } from '@mcp/index-documentation';
import { indexRepositoryTool, type IndexRepositoryInput } from '@mcp/index-repository';
import { listIndexedReposTool, type ListIndexedReposInput } from '@mcp/list-indexed-repos';
import { listServicesTool, type ListServicesInput } from '@mcp/list-services';
import { listWorkspacesTool, type ListWorkspacesInput } from '@mcp/list-workspaces';
import { searchAPIContractsTool, type SearchAPIContractsInput } from '@mcp/search-api-contracts';
import { searchCodebaseTool, type SearchCodebaseInput } from '@mcp/search-codebase';
import { deleteDocumentationTool, listDocumentationTool, searchReferencesTool } from '@mcp/search-documentation';
import { logger } from '@utils/logger';
import { type OllamaClient } from '@utils/ollama';
import { type CindexConfig } from '@/types/config';
import {
  type DeleteDocumentationInput,
  type IndexDocumentationInput,
  type ListDocumentationInput,
  type SearchReferencesInput,
} from '@/types/documentation';

/**
 * MCP tool return type
 *
 * Standardized response format for all MCP tools following the MCP SDK specification.
 * Content is always an array of text blocks for display, while structuredContent
 * provides machine-readable data for programmatic access.
 */
interface MCPToolResult {
  [key: string]: unknown; // Index signature for MCP SDK compatibility
  content: { type: 'text'; text: string }[]; // Formatted text output (Markdown)
  structuredContent?: Record<string, unknown>; // Structured data for programmatic access
}

/**
 * search_codebase MCP wrapper
 *
 * Wraps the search_codebase tool for MCP SDK compatibility. Returns formatted
 * Markdown search results and structured metadata for programmatic access.
 *
 * @param db - Database connection pool
 * @param config - cindex configuration
 * @param ollama - Ollama client for embeddings
 * @param input - Search parameters
 * @returns MCP-formatted result with content and structured metadata
 * @throws {Error} If search fails or validation errors occur
 */
export const searchCodebaseMCP = async (
  db: Pool,
  config: CindexConfig,
  ollama: OllamaClient,
  input: SearchCodebaseInput
): Promise<MCPToolResult> => {
  try {
    const result = await searchCodebaseTool(db, config, ollama, input);

    return {
      content: [
        {
          type: 'text',
          text: result.formatted_result,
        },
      ],
      structuredContent: {
        query: result.raw_result.query,
        query_type: result.raw_result.query_type,
        metadata: result.raw_result.metadata,
        warnings: result.raw_result.warnings,
        // Include actual file paths and chunks for visibility
        files: result.raw_result.context.relevant_files.map((f) => ({
          file_path: f.file_path,
          similarity: f.similarity,
          language: f.language,
          summary: f.file_summary,
        })),
        chunks: result.raw_result.context.code_locations.map((c) => ({
          file_path: c.file_path,
          start_line: c.start_line,
          end_line: c.end_line,
          similarity: c.similarity,
          chunk_type: c.chunk_type,
          content: c.chunk_content,
        })),
      },
    };
  } catch (error) {
    logger.error('search_codebase tool failed', { error });
    throw error;
  }
};

/**
 * get_file_context MCP wrapper
 *
 * Wraps the get_file_context tool for MCP SDK compatibility. Returns formatted
 * file context with callers, callees, and code chunks.
 *
 * @param db - Database connection pool
 * @param input - Get file context parameters
 * @returns MCP-formatted result with file metadata and dependency information
 * @throws {Error} If file not found or database query fails
 */
export const getFileContextMCP = async (db: Pool, input: GetFileContextInput): Promise<MCPToolResult> => {
  try {
    const result = await getFileContextTool(db, input);

    return {
      content: [
        {
          type: 'text',
          text: result.formatted_result,
        },
      ],
      structuredContent: {
        file_path: result.file.file_path,
        language: result.file.language,
        total_lines: result.file.total_lines,
        total_callers: result.total_callers,
        total_callees: result.total_callees,
        total_chunks: result.total_chunks,
      },
    };
  } catch (error) {
    logger.error('get_file_context tool failed', { error });
    throw error;
  }
};

/**
 * find_symbol_definition MCP wrapper
 *
 * Wraps the find_symbol_definition tool for MCP SDK compatibility. Returns formatted
 * symbol definitions and optional usage locations.
 *
 * @param db - Database connection pool
 * @param input - Find symbol parameters
 * @returns MCP-formatted result with symbol definitions and usage statistics
 * @throws {Error} If validation fails or database query fails
 */
export const findSymbolMCP = async (db: Pool, input: FindSymbolInput): Promise<MCPToolResult> => {
  try {
    const result = await findSymbolTool(db, input);

    return {
      content: [
        {
          type: 'text',
          text: result.formatted_result,
        },
      ],
      structuredContent: {
        symbol_name: input.symbol_name,
        total_definitions: result.symbols.length,
        total_usages: result.total_usages ?? 0,
        symbols: result.symbols.map((s) => ({
          name: s.symbol_name,
          type: s.symbol_type,
          file_path: s.file_path,
          line_number: s.line_number,
          scope: s.scope,
        })),
      },
    };
  } catch (error) {
    logger.error('find_symbol_definition tool failed', { error });
    throw error;
  }
};

/**
 * index_repository MCP wrapper
 *
 * Wraps the index_repository tool for MCP SDK compatibility. Returns formatted
 * indexing statistics with progress callback support for long-running operations.
 *
 * @param orchestrator - Indexing orchestrator instance
 * @param input - Index repository parameters
 * @param onProgress - Optional progress callback for MCP notifications
 * @returns MCP-formatted result with indexing statistics
 * @throws {Error} If repository path invalid or indexing fails
 */
export const indexRepositoryMCP = async (
  orchestrator: IndexingOrchestrator,
  input: IndexRepositoryInput,
  onProgress?: (progress: {
    stage: string;
    current: number;
    total: number;
    message: string;
    eta_seconds?: number;
  }) => void
): Promise<MCPToolResult> => {
  try {
    const result = await indexRepositoryTool(orchestrator, input, onProgress);

    return {
      content: [
        {
          type: 'text',
          text: result.formatted_result,
        },
      ],
      structuredContent: {
        repo_path: input.repo_path,
        repo_id: input.repo_id,
        files_indexed: result.stats.files_indexed,
        chunks_created: result.stats.chunks_created,
        symbols_extracted: result.stats.symbols_extracted,
        workspaces_detected: result.stats.workspaces_detected,
        services_detected: result.stats.services_detected,
        api_endpoints_found: result.stats.api_endpoints_found,
        indexing_time_ms: result.stats.indexing_time_ms,
      },
    };
  } catch (error) {
    logger.error('index_repository tool failed', { error });
    throw error;
  }
};

/**
 * delete_repository MCP wrapper
 *
 * Wraps the delete_repository tool for MCP SDK compatibility. Validates and deletes
 * repositories with their associated data (files, chunks, symbols, workspaces, services).
 *
 * @param db - Database connection pool
 * @param input - Delete repository parameters with repo_ids array
 * @returns MCP-formatted result with deletion statistics per repository
 * @throws {Error} If validation fails, repository not found, or deletion fails
 */
export const deleteRepositoryMCP = async (db: Pool, input: DeleteRepositoryInput): Promise<MCPToolResult> => {
  try {
    const result = await deleteRepositoryTool(db, input);
    const formattedResult = formatDeletionOutput(result);

    return {
      content: [
        {
          type: 'text',
          text: formattedResult,
        },
      ],
      structuredContent: {
        deleted: result.deleted,
        repositories: result.repositories,
      },
    };
  } catch (error) {
    logger.error('delete_repository tool failed', { error });
    throw error;
  }
};

/**
 * list_indexed_repos MCP wrapper
 */
export const listIndexedReposMCP = async (db: Pool, input: ListIndexedReposInput): Promise<MCPToolResult> => {
  try {
    const result = await listIndexedReposTool(db, input);

    return {
      content: [
        {
          type: 'text',
          text: result.formatted_result,
        },
      ],
      structuredContent: {
        total_count: result.total_count,
        repositories: result.repositories.map((repo) => ({
          repo_id: repo.repo_id,
          repo_type: repo.repo_type,
          file_count: repo.file_count,
          workspace_count: repo.workspace_count,
          service_count: repo.service_count,
          indexed_at: repo.indexed_at,
        })),
      },
    };
  } catch (error) {
    logger.error('list_indexed_repos tool failed', { error });
    throw error;
  }
};

/**
 * list_workspaces MCP wrapper
 */
export const listWorkspacesMCP = async (db: Pool, input: ListWorkspacesInput): Promise<MCPToolResult> => {
  try {
    const result = await listWorkspacesTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        total_count: result.total_count,
        workspaces: result.workspaces,
      },
    };
  } catch (error) {
    logger.error('list_workspaces tool failed', { error });
    throw error;
  }
};

/**
 * list_services MCP wrapper
 */
export const listServicesMCP = async (db: Pool, input: ListServicesInput): Promise<MCPToolResult> => {
  try {
    const result = await listServicesTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        total_count: result.total_count,
        services: result.services,
      },
    };
  } catch (error) {
    logger.error('list_services tool failed', { error });
    throw error;
  }
};

/**
 * get_workspace_context MCP wrapper
 */
export const getWorkspaceContextMCP = async (db: Pool, input: GetWorkspaceContextInput): Promise<MCPToolResult> => {
  try {
    const result = await getWorkspaceContextTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        workspace: result.context.workspace,
        dependencies_count: result.context.dependencies.length,
        dependents_count: result.context.dependents.length,
        files_count: result.context.files.length,
      },
    };
  } catch (error) {
    logger.error('get_workspace_context tool failed', { error });
    throw error;
  }
};

/**
 * get_service_context MCP wrapper
 */
export const getServiceContextMCP = async (db: Pool, input: GetServiceContextInput): Promise<MCPToolResult> => {
  try {
    const result = await getServiceContextTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        service: result.context.service,
        dependencies_count: result.context.dependencies.length,
        dependents_count: result.context.dependents.length,
        api_endpoints_count: result.context.api_endpoints.length,
        files_count: result.context.files.length,
      },
    };
  } catch (error) {
    logger.error('get_service_context tool failed', { error });
    throw error;
  }
};

/**
 * find_cross_workspace_usages MCP wrapper
 */
export const findCrossWorkspaceUsagesMCP = async (
  db: Pool,
  input: FindCrossWorkspaceUsagesInput
): Promise<MCPToolResult> => {
  try {
    const result = await findCrossWorkspaceUsagesTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        total_usages: result.total_usages,
        usages: result.usages,
      },
    };
  } catch (error) {
    logger.error('find_cross_workspace_usages tool failed', { error });
    throw error;
  }
};

/**
 * find_cross_service_calls MCP wrapper
 */
export const findCrossServiceCallsMCP = async (db: Pool, input: FindCrossServiceCallsInput): Promise<MCPToolResult> => {
  try {
    const result = await findCrossServiceCallsTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        total_calls: result.total_calls,
        calls: result.calls,
      },
    };
  } catch (error) {
    logger.error('find_cross_service_calls tool failed', { error });
    throw error;
  }
};

/**
 * search_api_contracts MCP wrapper
 */
export const searchAPIContractsMCP = async (
  db: Pool,
  ollama: OllamaClient,
  config: CindexConfig,
  input: SearchAPIContractsInput
): Promise<MCPToolResult> => {
  try {
    const result = await searchAPIContractsTool(db, ollama, config.embedding, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        query: input.query,
        total_results: result.total_results,
        endpoints: result.endpoints,
      },
    };
  } catch (error) {
    logger.error('search_api_contracts tool failed', { error });
    throw error;
  }
};

// Documentation Tools MCP Wrappers

/**
 * index_documentation MCP wrapper
 *
 * Indexes markdown files for documentation search.
 */
export const indexDocumentationMCP = async (
  db: Pool,
  ollama: OllamaClient,
  config: CindexConfig,
  input: IndexDocumentationInput
): Promise<MCPToolResult> => {
  try {
    const result = await indexDocumentationTool(db, ollama, config.embedding, input);

    return {
      content: [{ type: 'text', text: formatIndexDocumentationOutput(result) }],
      structuredContent: {
        success: result.success,
        doc_id: result.doc_id,
        files_indexed: result.files_indexed,
        files_skipped: result.files_skipped,
        chunks_created: result.chunks_created,
        code_blocks_indexed: result.code_blocks_indexed,
        sections_indexed: result.sections_indexed,
        errors: result.errors,
        duration_ms: result.duration_ms,
      },
    };
  } catch (error) {
    logger.error('index_documentation tool failed', { error });
    throw error;
  }
};

/**
 * search_references MCP wrapper
 *
 * Search reference materials including markdown documentation and reference repository code.
 */
export const searchReferencesMCP = async (
  db: Pool,
  ollama: OllamaClient,
  config: CindexConfig,
  input: SearchReferencesInput
): Promise<MCPToolResult> => {
  try {
    const result = await searchReferencesTool(db, ollama, config.embedding, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        query: result.output.query,
        total_results: result.output.total_results,
        search_time_ms: result.output.search_time_ms,
        doc_results: result.output.doc_results,
        code_results: result.output.code_results,
      },
    };
  } catch (error) {
    logger.error('search_references tool failed', { error });
    throw error;
  }
};

/**
 * list_documentation MCP wrapper
 *
 * List all indexed documentation.
 */
export const listDocumentationMCP = async (db: Pool, input: ListDocumentationInput): Promise<MCPToolResult> => {
  try {
    const result = await listDocumentationTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        total_documents: result.output.total_documents,
        total_chunks: result.output.total_chunks,
        documents: result.output.documents,
      },
    };
  } catch (error) {
    logger.error('list_documentation tool failed', { error });
    throw error;
  }
};

/**
 * delete_documentation MCP wrapper
 *
 * Delete indexed documentation by ID.
 */
export const deleteDocumentationMCP = async (db: Pool, input: DeleteDocumentationInput): Promise<MCPToolResult> => {
  try {
    const result = await deleteDocumentationTool(db, input);

    return {
      content: [{ type: 'text', text: result.formatted_result }],
      structuredContent: {
        success: result.output.success,
        deleted_doc_ids: result.output.deleted_doc_ids,
        chunks_deleted: result.output.chunks_deleted,
        files_deleted: result.output.files_deleted,
      },
    };
  } catch (error) {
    logger.error('delete_documentation tool failed', { error });
    throw error;
  }
};
